<template>
  <PageContainer :breadcrumbs="breadcrumbs">
    <Result/>
  </PageContainer>
</template>

<script lang="ts">
import { useSeoMeta, definePageMeta, useRuntimeConfig } from '#imports'
import { defineNuxtComponent, useNuxtApp, useRouter } from '#app'
import { DITokens } from '@/di'
import { storeToRefs } from 'pinia'
import { watch, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import Result from '@/containers/Pokemon/Search/Single/Result'
import type { FormFields } from '@/forms/pokemon/searchSingleProduct'
import { isIntString } from '@/utils/string'
import PageContainer from '@/containers/Page'

export default defineNuxtComponent({
  components: {
    Result,
    PageContainer,
  },
  setup() {
    const { public: { APP_ENV } } = useRuntimeConfig()

    const title = '商品検索 - カーナベル'
    const description = "ポケモンカード（ポケカ）の通販・販売ならカーナベル！人気シングルからコレクション向けカードまで豊富な在庫を状態別で販売！「納得価格」「状態別販売」「即日発送」で、最短翌日午前中に手に入る。"
    useSeoMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      robots: APP_ENV==='production' ? 'noindex,follow' :'noindex,nofollow',
      twitterSite: '@ka_nabell_pcg',
      ogSiteName: 'ポケカ販売ならカーナベル！',
    })

    definePageMeta({
      genre: 'pokemon',
      searchMode: 'detail',
      footer: {
        isShowSocialMedia: true,
        isShowShopSeries: true,
      }
    })

    const initialized = ref(false)
    const router = useRouter()
    const route = router.currentRoute.value
    const { $container } = useNuxtApp()
    const pokemonStore = $container.resolve(DITokens.PokemonStore)
    const {
      singleProductSearchFormFields,
      hasSingleProductSearchFormDetailFields,
      showSingleProductSearchDetailMenu,
    } = storeToRefs(pokemonStore)

    watch(singleProductSearchFormFields, val => {
      if (!process.client || !initialized.value) return

      // クエリパラメータを設定
      const query: { [key in keyof FormFields]: string | undefined } = {
        keyword: val.keyword || undefined,
        kindPokemon: val.kindPokemon ? 'true' : undefined,
        kindItem: val.kindItem ? 'true' : undefined,
        kindSupporter: val.kindSupporter ? 'true' : undefined,
        kindStadium: val.kindStadium ? 'true' : undefined,
        kindPokemonTool: val.kindPokemonTool ? 'true' : undefined,
        kindBasicEnergy: val.kindBasicEnergy ? 'true' : undefined,
        kindSpecialEnergy: val.kindSpecialEnergy ? 'true' : undefined,
        packId: val.packId ? String(val.packId) : undefined,
        rarityId: val.rarityId || undefined,
        regulationName: val.regulationName || undefined,
        sellingPriceGte: val.sellingPriceGte ? String(val.sellingPriceGte) : undefined,
        sellingPriceLte: val.sellingPriceLte ? String(val.sellingPriceLte) : undefined
      }

      // 検索を実行
      pokemonStore.searchSingleProduct(1)

      router.replace({ query })
    }, { deep: true })

    // browser back時に「initialized: true」が評価される場合があるため、ページ離脱時に強制的にリセット
    onBeforeUnmount(() => {
      initialized.value = false
    })

    // クエリパラメータから検索条件を初期設定
    onBeforeMount(() => {
      if (!process.client) return
      initialized.value = false
      const {
        page,
        keyword,
        kindPokemon,
        kindItem,
        kindSupporter,
        kindStadium,
        kindPokemonTool,
        kindBasicEnergy,
        kindSpecialEnergy,
        packId,
        rarityId,
        regulationName,
        sellingPriceGte,
        sellingPriceLte
      } = route.query

      singleProductSearchFormFields.value = {
        ...singleProductSearchFormFields.value,
        keyword: typeof keyword === 'string' ? keyword : '',
        kindPokemon: kindPokemon === 'true',
        kindItem: kindItem === 'true',
        kindSupporter: kindSupporter === 'true',
        kindStadium: kindStadium === 'true',
        kindPokemonTool: kindPokemonTool === 'true',
        kindBasicEnergy: kindBasicEnergy === 'true',
        kindSpecialEnergy: kindSpecialEnergy === 'true',
        packId: typeof packId === 'string' && isIntString(packId) ? parseInt(packId, 10) : null,
        rarityId: typeof rarityId === 'string' ? rarityId : null,
        regulationName: typeof regulationName === 'string' ? regulationName : '',
        sellingPriceGte: typeof sellingPriceGte === 'string' && isIntString(sellingPriceGte) ? parseInt(sellingPriceGte, 10) : null,
        sellingPriceLte: typeof sellingPriceLte === 'string' && isIntString(sellingPriceLte) ? parseInt(sellingPriceLte, 10) : null
      }

      showSingleProductSearchDetailMenu.value = hasSingleProductSearchFormDetailFields.value

      let _page = 1
      if (typeof page === 'string' && isIntString(page)) {
        _page = parseInt(page, 10)
      }

      // 検索を実行
      pokemonStore.searchSingleProduct(_page).then(() => initialized.value = true)
    })

    const breadcrumbs = [
      {
        title: 'トレカ販売トップ',
        link: '/',
      },
      {
        title: 'ポケカ販売トップ',
        link: '/pokemon',
      },
      {
        title: '商品検索',
      },
    ]

    return {
      breadcrumbs,
    }
  }
})
</script>
